import { yupResolver } from "@hookform/resolvers/yup";
import Button from "components/Button";
import Input from "components/Input";
import { useToaster } from "contexts/toasterContext";
import React, { useState } from "react";
import { preventSpecialCharKeyDown } from "util/preventSpecialCharInput";
import { FormProvider, useForm } from "react-hook-form";
import apiService from "services/apiService";
import * as yup from "yup";

const schema = yup.object().shape({
  financialYear: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null || originalValue === undefined
        ? undefined
        : value,
    )
    .typeError("Year is required")
    .integer("Financial Year must be a whole number")
    .min(2000, "Financial Year must be 2000 or later")
    .max(new Date().getFullYear() + 1, "Invalid Financial Year")
    .required("Year is required"),
  slabStart: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null || originalValue === undefined
        ? undefined
        : value,
    )
    .typeError("Slab is required")
    .moreThan(0, "Tax Slab must be greater than 0")
    .required("Slab is required"),
  taxRate: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null || originalValue === undefined
        ? undefined
        : value,
    )
    .typeError("Tax Rate is required")
    .moreThan(0, "Tax Rate must be greater than 0")
    .max(100, "Tax Rate cannot exceed 100%")
    .required("Tax Rate is required"),
  cessRate: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null || originalValue === undefined
        ? undefined
        : value,
    )
    .typeError("Cess Rate Is required")
    .moreThan(0, "Cess Rate must be greater than 0")
    .max(100, "Cess Rate cannot exceed 100%")
    .required("Cess Rate Is required"),
});

type TaxProps = yup.InferType<typeof schema>;
interface AddProps {
  onClose: () => void;
  onSuccess?: (message: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setRefresh: any;
  refresh: number;
}

const AddTaxConfig = ({ onClose, setRefresh, refresh }: AddProps) => {
  const { addToast } = useToaster();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<TaxProps>({
    resolver: yupResolver(schema),
    defaultValues: {
      financialYear: new Date().getFullYear(),
      // Explicitly define empty defaults to prevent false dirty states when clearing inputs
      slabStart: "" as unknown as number,
      taxRate: "" as unknown as number,
      cessRate: "" as unknown as number,
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  // Extract isDirty to track form modifications
  const { isDirty } = methods.formState;

  const submit = (taskData: TaxProps) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    console.log(taskData);

    apiService
      .post("api/tax/addd", taskData)
      .then((response) => {
        console.log(response);

        setRefresh(refresh + 1);
        addToast("Tax Config is Added successfully", "success");
      })
      .catch((error) => {
        console.log(error);
        addToast(error?.response?.data?.message || "Failed to add tax config", "error");
      })
      .finally(() => {
        setIsSubmitting(false);
        onClose();
      });
  };

  return (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(submit)}>
          <section className="flex flex-col gap-1 p-4">
            <Input
              name={"financialYear"}
              label={"Financial Year"}
              placeholder={"Enter Financial Year"}
              type={"number"}
              required
              min={2000}
              max={new Date().getFullYear() + 1}
            ></Input>
            <Input
              name={"slabStart"}
              label={"Tax Slab"}
              placeholder={"Enter Tax Slab"}
              type={"number"}
              required
              min={1}
              onKeyDown={preventSpecialCharKeyDown}
            ></Input>
            <Input
              name={"taxRate"}
              label={"Tax Rate"}
              placeholder={"Enter Tax Rate (%)"}
              type={"number"}
              required
              min={1}
              max={100}
              onKeyDown={preventSpecialCharKeyDown}
            ></Input>
            <Input
              name={"cessRate"}
              label={"Cess Rate"}
              placeholder={"Enter Cess Rate (%)"}
              type={"number"}
              required
              min={1}
              max={100}
              onKeyDown={preventSpecialCharKeyDown}
            ></Input>
          </section>
          <section className="mt-2 flex justify-end px-4">
            <Button
              type="button"
              onClick={onClose}
              className="!bg-white !text-slate-600 border border-slate-200 hover:!bg-slate-100 focus:!ring-4 focus:!outline-none focus:!ring-slate-200 transition-all active:scale-95 mr-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !isDirty}
              loading={isSubmitting}
              className="!text-white !bg-blue-700 hover:!bg-blue-800 focus:!ring-4 focus:!outline-none focus:!ring-blue-300 dark:!bg-blue-600 dark:hover:!bg-blue-700 dark:focus:!ring-blue-800 disabled:!opacity-50 disabled:!cursor-not-allowed"
            >
              Submit
            </Button>
          </section>
        </form>
      </FormProvider>
    </div>
  );
};

export default AddTaxConfig;